import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, FileText, ChevronLeft, ChevronRight, Reply, Forward, Star, Trash2, SmilePlus } from 'lucide-react';
import { downloadFile } from '../utils/downloadFile';
import { REACTION_EMOJIS } from './MessageActionsMenu';

const OFFICE_TYPES = ['excel', 'word', 'powerpoint'];

const HeaderButton = ({ onClick, title, children, active }) => (
  <button
    onClick={onClick}
    style={{
      background: active ? 'rgba(46,124,186,0.5)' : 'rgba(255,255,255,0.1)',
      border: 'none',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      cursor: 'pointer',
      transition: 'background 0.2s ease',
      flexShrink: 0,
    }}
    onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
    onMouseOut={(e) => { e.currentTarget.style.background = active ? 'rgba(46,124,186,0.5)' : 'rgba(255,255,255,0.1)'; }}
    title={title}
    aria-label={title}
  >
    {children}
  </button>
);

/**
 * Unified media viewer (lightbox). For images inside a chat it supports
 * navigation between all chat images plus the full WhatsApp-style action
 * row: download, reply, forward, star, delete, emoji reaction. Every action
 * prop is optional — contexts without a message (e.g. plain previews) simply
 * render close + download.
 */
const MediaViewerModal = ({
  mediaUrl,
  mediaType,
  mediaName,
  onClose,
  isRTL,
  t = {},
  // Image navigation across the chat's image messages
  images = null,          // [{ message }] or message array
  currentIndex = -1,
  onNavigate = null,      // (newIndex) => void
  // Message actions
  message = null,
  onReply = null,
  onForward = null,
  onToggleStar = null,
  isStarred = false,
  onDelete = null,
  onReact = null,
}) => {
  const [showEmojiBar, setShowEmojiBar] = useState(false);

  const canNavigate = Array.isArray(images) && images.length > 1 && typeof onNavigate === 'function';
  const goPrev = () => canNavigate && onNavigate((currentIndex - 1 + images.length) % images.length);
  const goNext = () => canNavigate && onNavigate((currentIndex + 1) % images.length);

  // Prevent body scroll when open + keyboard navigation
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, images]);

  useEffect(() => {
    setShowEmojiBar(false);
  }, [mediaUrl]);

  if (!mediaUrl) return null;

  const isOffice = OFFICE_TYPES.includes(mediaType);
  // Office files can't be rendered natively by the browser; use the Microsoft
  // Office online viewer (needs the file URL to be publicly reachable). The
  // download button always works as a fallback.
  const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(mediaUrl)}`;

  const isImage = mediaType === 'image';

  return createPortal(
    <div
      data-modal-root="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999999,
        background: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex',
        flexDirection: 'column',
        direction: isRTL ? 'rtl' : 'ltr',
      }}
    >
      {/* Header */}
      <div style={{
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 100%)',
      }}>
        <div style={{ color: '#fff', fontSize: '16px', fontWeight: '600', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {mediaName || (isImage ? 'Image' : 'Document')}
          {canNavigate && (
            <span style={{ fontSize: '13px', opacity: 0.7, marginInlineStart: '10px', direction: 'ltr', display: 'inline-block' }}>
              {currentIndex + 1} / {images.length}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '10px', position: 'relative' }}>
          {isImage && message && onReact && (
            <HeaderButton onClick={() => setShowEmojiBar((p) => !p)} title="Emoji" active={showEmojiBar}>
              <SmilePlus size={19} />
            </HeaderButton>
          )}
          {isImage && message && onReply && (
            <HeaderButton onClick={() => { onReply(message); onClose(); }} title={t.reply}>
              <Reply size={19} style={{ transform: isRTL ? 'scaleX(-1)' : 'none' }} />
            </HeaderButton>
          )}
          {isImage && message && onForward && (
            <HeaderButton onClick={() => { onForward(message); onClose(); }} title={t.forwardAction}>
              <Forward size={19} style={{ transform: isRTL ? 'scaleX(-1)' : 'none' }} />
            </HeaderButton>
          )}
          {isImage && message && onToggleStar && (
            <HeaderButton onClick={() => onToggleStar(message)} title={isStarred ? t.unstarAction : t.starAction} active={isStarred}>
              <Star size={19} fill={isStarred ? '#f59e0b' : 'none'} color={isStarred ? '#f59e0b' : '#fff'} />
            </HeaderButton>
          )}
          {isImage && message && onDelete && (
            <HeaderButton onClick={() => { onDelete(message); onClose(); }} title={t.deleteAction}>
              <Trash2 size={19} />
            </HeaderButton>
          )}
          <HeaderButton onClick={() => downloadFile(mediaUrl, mediaName)} title={t.download || 'Download'}>
            <Download size={20} />
          </HeaderButton>
          <HeaderButton onClick={onClose} title={t.cancel || 'Close'}>
            <X size={20} />
          </HeaderButton>

          {/* Emoji quick bar */}
          {showEmojiBar && (
            <div
              style={{
                position: 'absolute',
                top: '48px',
                insetInlineEnd: 0,
                display: 'flex',
                gap: '4px',
                background: 'rgba(30, 41, 59, 0.95)',
                borderRadius: '24px',
                padding: '6px 10px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                zIndex: 5,
              }}
            >
              {REACTION_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => { onReact(message, emoji); setShowEmojiBar(false); }}
                  style={{
                    border: 'none', background: 'transparent', cursor: 'pointer',
                    fontSize: '20px', lineHeight: 1, padding: '4px', borderRadius: '50%',
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        overflow: 'hidden',
        position: 'relative',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      >
        {/* Physical-side navigation arrows (LTR icon semantics kept — they
            navigate the list, keyboard arrows mirror them). */}
        {isImage && canNavigate && (
          <>
            <button
              onClick={goPrev}
              aria-label="Previous"
              style={{
                position: 'absolute', left: '18px', top: '50%', transform: 'translateY(-50%)',
                width: '46px', height: '46px', borderRadius: '50%', border: 'none',
                background: 'rgba(255,255,255,0.12)', color: '#fff', display: 'flex',
                alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 3,
              }}
              onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; }}
              onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
            >
              <ChevronLeft size={26} />
            </button>
            <button
              onClick={goNext}
              aria-label="Next"
              style={{
                position: 'absolute', right: '18px', top: '50%', transform: 'translateY(-50%)',
                width: '46px', height: '46px', borderRadius: '50%', border: 'none',
                background: 'rgba(255,255,255,0.12)', color: '#fff', display: 'flex',
                alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 3,
              }}
              onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; }}
              onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
            >
              <ChevronRight size={26} />
            </button>
          </>
        )}

        {isImage ? (
          <img
            src={mediaUrl}
            alt={mediaName || 'Image preview'}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
            }}
          />
        ) : mediaType === 'pdf' ? (
          <iframe
            src={mediaUrl}
            title={mediaName || 'PDF Viewer'}
            style={{
              width: '100%',
              height: '100%',
              maxWidth: '1000px',
              border: 'none',
              borderRadius: '12px',
              backgroundColor: '#fff',
              boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
            }}
          />
        ) : isOffice ? (
          <div style={{ width: '100%', height: '100%', maxWidth: '1000px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <iframe
              src={officeViewerUrl}
              title={mediaName || 'Document Viewer'}
              style={{
                flex: 1,
                width: '100%',
                border: 'none',
                borderRadius: '12px',
                backgroundColor: '#fff',
                boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
              }}
            />
            <button
              onClick={() => downloadFile(mediaUrl, mediaName)}
              style={{
                alignSelf: 'center',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 22px',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #2e7cba, #1a6baa)',
                color: '#fff',
                fontWeight: '700',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              <Download size={16} />
              {isRTL ? 'تحميل الملف' : 'Download file'}
            </button>
          </div>
        ) : (
          <div style={{ color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
            <FileText size={64} style={{ opacity: 0.7 }} />
            <div style={{ fontSize: '15px', opacity: 0.85 }}>
              {isRTL ? 'لا يمكن معاينة هذا الملف — يمكنك تحميله' : 'This file cannot be previewed — you can download it'}
            </div>
            <button
              onClick={() => downloadFile(mediaUrl, mediaName)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 22px',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #2e7cba, #1a6baa)',
                color: '#fff',
                fontWeight: '700',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              <Download size={16} />
              {isRTL ? 'تحميل الملف' : 'Download file'}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default MediaViewerModal;
